/**
 * 入住单住客
 */
import React from 'react';
import { Input, Select, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import cls from 'classnames';
import intl from 'react-intl-universal';

import { FormItem } from '@/components/Reservation/ContentBlock';
import BaseMobileInput from '@/components/BaseMobileInput';
import BaseEmailInput from '@/components/BaseEmailInput';
import { CertIDType } from '@/config/constant';
import IconFont from '@/components/IconFont';
import { TenantGlobalConfig } from '@/components/Intl/IntlContext/helper';
import { isNil, parseFen2Currency } from '@/utils/number';
import { CheckInGuestInfo } from '@/redux/reducers/reservation/base.d';
import { CardItem, MemberLevelItem } from '@/redux/reducers/guest';
import { formatAreaMobileStr } from '@/components/BaseMobileInput/helper';

import RemarkInput from './RemarkInput';
import styles from './index.module.scss';

interface IProps {
  isMatser?: boolean;
  memberLevelMap: { [key: string]: MemberLevelItem };
  readOnly: boolean; // 是否只读
  showRecharge: boolean; // 是否显示充值按钮
  showCardPurchase: boolean; // 是否显示会员卡购买按钮
  canBuyCardMap: { [key: string]: Array<CardItem> };
  data: CheckInGuestInfo;
  onChange: (indexId: string, updateValue: Partial<CheckInGuestInfo>) => void;
  setToMaster?: (indexId: string) => void;
  refreshCustomer?: (indexId: string) => void;
  deleteCustomer?: (indexId: string) => void;
  getPopContainer: () => HTMLElement;
  checkMemberById: (indexId: string) => void;
  showCardPurchaseModal?: (value: CheckInGuestInfo) => void;
  showCardRechargeModal?: (value: CheckInGuestInfo) => void;
  showGuestDetailModal: (data: CheckInGuestInfo) => void;
}

const Index: React.FC<IProps> = (props) => {
  const {
    isMatser = false,
    memberLevelMap,
    readOnly,
    showRecharge,
    canBuyCardMap,
    data,
    onChange,
    setToMaster,
    refreshCustomer,
    deleteCustomer,
    getPopContainer,
    checkMemberById,
    showCardPurchaseModal,
    showCardRechargeModal,
    showGuestDetailModal,
  } = props;
  const itemId = data.indexId;
  const isReadInfo = data.source === 'READ'; // 是否是读取的

  const updateCustomerInfo: (value: Partial<CheckInGuestInfo>) => void = (value) => {
    onChange(itemId, value);
  };

  // 宾客人姓名变更
  const handleNameChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value || '';
    // if (value.length > 32) return;
    updateCustomerInfo({ name: value });
  };

  // 宾客手机号变更
  const handlePhoneChange: (mobile: string) => void = (mobile) => {
    // if (!/^\d{0,11}$/.test(mobile)) return;
    updateCustomerInfo({ mobile });
    // wyuid不存在时 根据手机号匹配是否会员
    // if (PHONE_REG.test(mobile) && !data.wyuId) {
    //   checkMemberByPhone(mobile);
    // }
  };

  const handleEmailChange = (email) => {
    updateCustomerInfo({ email });
  };

  const autoCheckInfo: React.FocusEventHandler<HTMLInputElement> = (event) => {
    if (event.relatedTarget) {
      // checkReserveOrder(data);
      checkMemberById(itemId);
    }
  };

  // 证件类型变更
  const handleCredentialTypeChange: (certType: CertIDType) => void = (certType) => {
    updateCustomerInfo({ certType });
    checkMemberById(itemId);
  };

  // 住客证件号变更
  const handleCredentialNoChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value || '';
    updateCustomerInfo({ certNo: value });
  };

  // 宾客备注变更
  const handleRemarkChange: (val: string) => void = (val) => {
    updateCustomerInfo({ remark: val });
  };

  const renderMemberInfo = (showAction) => {
    const { memberLevel, wyuId } = data;
    const isCanBuy = Array.isArray(canBuyCardMap[wyuId]) && !!canBuyCardMap[wyuId]?.length;
    const newMemberBtn = isCanBuy ? (
      <Button className={styles.newMemberBtn} onClick={() => showCardPurchaseModal(data)}>
        {intl.get('drawer_checkin_guest_buy_card_btn')}
      </Button>
    ) : null;
    const rechargeBtn = showRecharge ? (
      <Button className={styles.rechargeBtn} onClick={() => showCardRechargeModal(data)}>
        {intl.get('drawer_checkin_guest_recharge_btn')}
      </Button>
    ) : null;
    return (
      <div className={styles.memberInfo}>
        {!isNil(memberLevel) ? <span>{(memberLevelMap || {})[memberLevel]?.name}</span> : '—'}
        {!isNil(memberLevel) ? newMemberBtn : null}
        <span className={styles.balanceValue}>
          {intl.get('drawer_checkin_guest_balance_amount', {
            amount: !isNil(memberLevel) ? parseFen2Currency(data.balance || 0) : '—',
          })}
        </span>
        {!isNil(memberLevel) ? rechargeBtn : null}
      </div>
    );
  };

  if (readOnly) {
    return (
      <div className={styles.readonlyCustomer}>
        <FormItem label={intl.get('drawer_checkin_guest_name_label')}>{data.name}</FormItem>
        <FormItem label={intl.get('app_reservation_cert_type')} className={styles.largeItem}>
          {TenantGlobalConfig.getSystemConfig().certTypesMap?.[data.certType]}
        </FormItem>
        <FormItem label={intl.get('app_reservation_cert_number')}>{data.certNo}</FormItem>
        <FormItem label={intl.get('app_reservation_phone_number')}>
          {formatAreaMobileStr(data.mobile)}
        </FormItem>
        <br />
        <FormItem label={intl.get('help_store_address_type_email')}>{data.email}</FormItem>
        <FormItem label={intl.get('app_reservation_member_level')} className={styles.largeItem}>
          {renderMemberInfo(false)}
        </FormItem>
        <FormItem label={intl.get('drawer_checkin_guest_remark_label')}>{data.remark}</FormItem>
      </div>
    );
  }

  const showMasterBtn = data.certType === CertIDType.ID_CARD && data.certNo && setToMaster;

  return (
    <div className={styles.infoContainer}>
      <div className={styles.leftWrapper}>
        <FormItem label={intl.get('drawer_checkin_guest_name_label')}>
          <Input
            placeholder={intl.get('drawer_checkin_guest_name_placeholder')}
            maxLength={32}
            value={data.name}
            disabled={isReadInfo}
            onChange={handleNameChange}
            onBlur={autoCheckInfo}
          />
        </FormItem>
        <FormItem label={intl.get('app_reservation_cert_number')} className={styles.largeItem}>
          <div className={styles.certNoInfo}>
            <Select
              getPopupContainer={getPopContainer}
              options={TenantGlobalConfig.getSystemConfig().certTypesDataSource}
              onChange={handleCredentialTypeChange}
              value={data.certType}
              disabled={isReadInfo}
            />
            <Input
              placeholder={intl.get('drawer_checkin_guest_cert_placeholder')}
              value={data.certNo}
              onChange={handleCredentialNoChange}
              onBlur={autoCheckInfo}
              disabled={isReadInfo}
            />
          </div>
        </FormItem>
        <FormItem label={intl.get('app_reservation_phone_number')}>
          <BaseMobileInput
            isKeyPerson={isMatser}
            value={data.mobile}
            onChange={handlePhoneChange}
          />
        </FormItem>
        <br />
        <FormItem label={intl.get('help_store_address_type_email')}>
          <BaseEmailInput
            isKeyPerson={isMatser}
            value={data.email}
            onChange={handleEmailChange}
            style={{ width: 240 }}
          />
        </FormItem>
        <FormItem label={intl.get('app_reservation_member_level')} className={styles.largeItem}>
          {renderMemberInfo(true)}
        </FormItem>
        <FormItem
          label={intl.get('drawer_checkin_guest_remark_label')}
          className={cls(styles.largeItem, styles.remarkItem)}
        >
          <span className={styles.remarkContent}>{data.remark}</span>
          <RemarkInput
            value={data.remark}
            getPopContainer={getPopContainer}
            onChange={handleRemarkChange}
          />
        </FormItem>
        {isReadInfo ? (
          <FormItem label="" className={styles.detailItem}>
            <Button onClick={() => showGuestDetailModal(data)}>
              {intl.get('drawer_checkin_guest_detail_btn')}
            </Button>
          </FormItem>
        ) : null}
      </div>
      <div className={styles.actionWrapper}>
        {showMasterBtn ? (
          <span className={styles.setRoleTrigger} onClick={() => setToMaster(itemId)}>
            <IconFont type="icon-zhiding" />
            {intl.get('drawer_checkin_guest_set_master_btn')}
          </span>
        ) : null}
        {refreshCustomer ? (
          <span className={styles.deleteTrigger} onClick={() => refreshCustomer(itemId)}>
            <IconFont type="icon-shuaxin" />
          </span>
        ) : null}
        {deleteCustomer ? (
          <span className={styles.deleteTrigger} onClick={() => deleteCustomer(itemId)}>
            <CloseOutlined />
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default Index;
